import JSBI from 'jsbi';
import Decimal from 'decimal.js';

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var ZERO = /*#__PURE__*/JSBI.BigInt(0);
var ONE = /*#__PURE__*/JSBI.BigInt(1);
var TWO = /*#__PURE__*/JSBI.BigInt(2);
function abs(number) {
  if (JSBI.GE(number, ZERO)) {
    return number;
  }
  return JSBI.unaryMinus(number);
}
function sumArray(numbers) {
  return numbers.reduce(function (prev, curr) {
    return JSBI.add(prev, curr);
  }, ZERO);
}
function mulArray(numbers) {
  return numbers.reduce(function (prev, curr) {
    return JSBI.multiply(prev, curr);
  }, ONE);
}
function ceilingDivision(dividend, divisor) {
  var quotient = JSBI.divide(dividend, divisor);
  if (JSBI.equal(quotient, ZERO)) {
    return [ZERO, ZERO];
  }
  var remainder = JSBI.remainder(dividend, divisor);
  if (JSBI.greaterThan(remainder, ZERO)) {
    quotient = JSBI.add(quotient, ONE);
    divisor = JSBI.divide(dividend, quotient);
    remainder = JSBI.remainder(dividend, quotient);
    if (JSBI.greaterThan(remainder, ZERO)) {
      divisor = JSBI.add(divisor, ONE);
    }
  }
  return [quotient, divisor];
}
function toDecimal(input, shift) {
  if (shift === void 0) {
    shift = 0;
  }
  return new Decimal(input.toString()).div(new Decimal(10).pow(shift));
}

var MAX_ITERATIONS = 32;
var Curve = /*#__PURE__*/function () {
  function Curve(numberOfCurrencies, amplificationFactor, targetPrices) {
    this.numberOfCurrencies = void 0;
    this.amplificationFactor = void 0;
    this.targetPrices = void 0;
    this.numberOfCurrencies = numberOfCurrencies;
    this.amplificationFactor = amplificationFactor;
    this.targetPrices = targetPrices;
  }
  var _proto = Curve.prototype;
  _proto.exchange = function exchange(tokenAmounts, inputIndex, outputIndex, amount, minusOne) {
    if (minusOne === void 0) {
      minusOne = true;
    }
    if (tokenAmounts.length !== JSBI.toNumber(this.numberOfCurrencies)) {
      throw new Error('Number of currencies does not match');
    }
    var xp = this.xp(tokenAmounts);
    var dx = JSBI.multiply(amount, this.targetPrices[inputIndex]);
    var x = JSBI.add(xp[inputIndex], dx);
    var y = this.computeY(tokenAmounts, inputIndex, outputIndex, x);
    var dy = JSBI.subtract(xp[outputIndex], y);
    // This is a special condition on Curve stable algo. For TokenSwap, they don't seem to apply this minus one.
    if (minusOne) {
      dy = JSBI.subtract(dy, ONE);
    }
    return JSBI.divide(dy, this.targetPrices[outputIndex]);
  };
  _proto.computeBaseY = function computeBaseY(tokenAmounts, inputIndex, outputIndex, amount) {
    var d = this.computeD(tokenAmounts);
    var xp = this.xp(tokenAmounts);
    var nn = JSBI.exponentiate(this.numberOfCurrencies, this.numberOfCurrencies);
    var sum = sumArray(xp);
    var product = mulArray(xp);
    var k = JSBI.subtract(JSBI.add(JSBI.multiply(JSBI.multiply(this.amplificationFactor, nn), sum), d), JSBI.multiply(JSBI.multiply(this.amplificationFactor, d), nn));
    var b = JSBI.multiply(JSBI.multiply(JSBI.multiply(this.amplificationFactor, nn), nn), product);
    var c = JSBI.multiply(JSBI.multiply(nn, product), k);
    var numerator = JSBI.add(b, JSBI.divide(c, xp[inputIndex]));
    var denominator = JSBI.add(b, JSBI.divide(c, xp[outputIndex]));
    // Convert to number since JSBI doesn't support log10
    var inputFactor = Math.log10(JSBI.toNumber(this.targetPrices[inputIndex]));
    var outputFactor = Math.log10(JSBI.toNumber(this.targetPrices[outputIndex]));
    var factor = Math.abs(outputFactor - inputFactor);
    if (inputFactor >= outputFactor) {
      return JSBI.BigInt(new Decimal(numerator.toString()).mul(new Decimal(amount.toString())).div(new Decimal(denominator.toString())).mul(Math.pow(10, factor)).floor().toString());
    } else {
      return JSBI.BigInt(new Decimal(numerator.toString()).mul(new Decimal(amount.toString())).div(new Decimal(denominator.toString())).div(Math.pow(10, factor)).floor().toString());
    }
  };
  _proto.computeY = function computeY(tokenAmounts, inputIndex, outputIndex, newTotalAmount) {
    var d = this.computeD(tokenAmounts);
    var xx = this.xp(tokenAmounts);
    xx[inputIndex] = newTotalAmount;
    xx.splice(outputIndex, 1);
    var ann = JSBI.multiply(this.amplificationFactor, this.numberOfCurrencies);
    var c = d;
    for (var _iterator = _createForOfIteratorHelperLoose(xx), _step; !(_step = _iterator()).done;) {
      var _y = _step.value;
      c = JSBI.divide(JSBI.multiply(c, d), JSBI.multiply(_y, this.numberOfCurrencies));
    }
    c = JSBI.divide(JSBI.multiply(c, d), JSBI.multiply(this.numberOfCurrencies, ann));
    var b = JSBI.subtract(JSBI.add(sumArray(xx), JSBI.divide(d, ann)), d);
    var yPrev = ZERO;
    var y = d;
    for (var i = 0; i < MAX_ITERATIONS; i++) {
      yPrev = y;
      y = JSBI.divide(JSBI.add(JSBI.exponentiate(y, TWO), c), JSBI.add(JSBI.multiply(TWO, y), b));
      if (JSBI.greaterThan(y, yPrev)) {
        if (JSBI.lessThanOrEqual(JSBI.subtract(y, yPrev), ONE)) {
          break;
        }
      } else {
        if (JSBI.lessThanOrEqual(JSBI.subtract(yPrev, y), ONE)) {
          break;
        }
      }
    }
    return y;
  };
  _proto.computeD = function computeD(tokenAmounts) {
    var dPrev = ZERO;
    var xp = this.xp(tokenAmounts);
    var sum = sumArray(xp);
    var d = sum;
    var ann = JSBI.multiply(this.amplificationFactor, this.numberOfCurrencies);
    for (var i = 0; i < MAX_ITERATIONS; i++) {
      var dP = d;
      for (var _iterator2 = _createForOfIteratorHelperLoose(xp), _step2; !(_step2 = _iterator2()).done;) {
        var x = _step2.value;
        dP = JSBI.divide(JSBI.multiply(dP, d), JSBI.multiply(this.numberOfCurrencies, x));
      }
      dPrev = d;
      var numerator = JSBI.multiply(JSBI.add(JSBI.multiply(ann, sum), JSBI.multiply(dP, this.numberOfCurrencies)), d);
      var denominator = JSBI.add(JSBI.multiply(JSBI.subtract(ann, ONE), d), JSBI.multiply(JSBI.add(this.numberOfCurrencies, ONE), dP));
      d = JSBI.divide(numerator, denominator);
      if (JSBI.greaterThan(d, dPrev)) {
        if (JSBI.lessThanOrEqual(JSBI.subtract(d, dPrev), ONE)) {
          break;
        }
      } else {
        if (JSBI.lessThanOrEqual(JSBI.subtract(dPrev, d), ONE)) {
          break;
        }
      }
    }
    return d;
  };
  _proto.xp = function xp(tokenAmounts) {
    var _this = this;
    return tokenAmounts.map(function (tokenAmount, index) {
      return JSBI.multiply(tokenAmount, _this.targetPrices[index]);
    });
  };
  _proto.setAmplificationFactor = function setAmplificationFactor(amplificationFactor) {
    this.amplificationFactor = amplificationFactor;
  };
  return Curve;
}();

var Stable = /*#__PURE__*/function () {
  function Stable(numberOfCurrencies, amp, targetPrices, traderFee) {
    this.targetPrices = void 0;
    this.traderFee = void 0;
    this.curve = void 0;
    this.targetPrices = targetPrices;
    this.traderFee = traderFee;
    this.curve = new Curve(numberOfCurrencies, amp, this.targetPrices);
  }
  var _proto = Stable.prototype;
  _proto.exchange = function exchange(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    var outputAmountWithoutFees = this.getOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex);
    var fees = this.getFees(outputAmountWithoutFees);
    var expectedOutputAmount = JSBI.subtract(outputAmountWithoutFees, fees);
    return {
      priceImpact: this.getPriceImpact(tokenAmounts, inputTradeAmount, expectedOutputAmount, inputIndex, outputIndex),
      fees: fees,
      expectedOutputAmount: expectedOutputAmount
    };
  };
  _proto.getPriceImpact = function getPriceImpact(tokenAmounts, inputTradeAmountJSBI, expectedOutputAmountJSBI, inputIndex, outputIndex) {
    if (JSBI.equal(inputTradeAmountJSBI, ZERO) || JSBI.equal(tokenAmounts[inputIndex], ZERO) || JSBI.equal(tokenAmounts[outputIndex], ZERO)) {
      return new Decimal(0);
    }
    var noSlippageOutputAmount = toDecimal(this.getOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmountJSBI, inputIndex, outputIndex));
    var expectedOutputAmount = toDecimal(expectedOutputAmountJSBI);
    var impact = noSlippageOutputAmount.sub(expectedOutputAmount).div(noSlippageOutputAmount);
    return impact;
  };
  _proto.getFees = function getFees(outputAmountWithoutFees) {
    return JSBI.equal(this.traderFee.numerator, ZERO) ? ZERO : JSBI.divide(JSBI.multiply(outputAmountWithoutFees, this.traderFee.numerator), this.traderFee.denominator);
  };
  _proto.getOutputAmount = function getOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    return this.curve.exchange(tokenAmounts, inputIndex, outputIndex, inputTradeAmount);
  };
  _proto.getOutputAmountWithNoSlippage = function getOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    return this.curve.computeBaseY(tokenAmounts, inputIndex, outputIndex, inputTradeAmount);
  };
  _proto.setAmp = function setAmp(amp) {
    this.curve.setAmplificationFactor(amp);
  };
  _proto.setTraderFee = function setTraderFee(traderFee) {
    this.traderFee = traderFee;
  };
  return Stable;
}();

/** calculates fee amount given a fee fraction, returning one when fee is set but amount is zero */
function calculateFeeAmount(amount, fee) {
  if (JSBI.equal(fee.numerator, ZERO) || JSBI.equal(amount, ZERO)) {
    return ZERO;
  } else {
    var feeAmount = JSBI.divide(JSBI.multiply(amount, fee.numerator), fee.denominator);
    if (JSBI.equal(feeAmount, ZERO)) {
      return ONE;
    }
    return feeAmount;
  }
}

var TokenSwapStable = /*#__PURE__*/function () {
  function TokenSwapStable(amp, traderFee, ownerFee) {
    this.traderFee = void 0;
    this.ownerFee = void 0;
    this.curve = void 0;
    this.traderFee = traderFee;
    this.ownerFee = ownerFee;
    this.curve = new Curve(TWO, amp, [ONE, ONE]);
  }
  var _proto = TokenSwapStable.prototype;
  _proto.exchange = function exchange(tokenAmounts, inputTradeAmount, outputIndex) {
    var inputIndex = outputIndex === 0 ? 1 : 0;
    var expectedOutputAmount = this.getExpectedOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex);
    return {
      priceImpact: this.getPriceImpact(tokenAmounts, inputTradeAmount, expectedOutputAmount, inputIndex, outputIndex),
      fees: this.getFees(inputTradeAmount),
      expectedOutputAmount: expectedOutputAmount
    };
  };
  _proto.getPriceImpact = function getPriceImpact(tokenAmounts, inputTradeAmountJSBI, expectedOutputAmountJSBI, inputIndex, outputIndex) {
    if (JSBI.equal(inputTradeAmountJSBI, ZERO) || JSBI.equal(tokenAmounts[inputIndex], ZERO) || JSBI.equal(tokenAmounts[outputIndex], ZERO)) {
      return new Decimal(0);
    }
    var noSlippageOutputAmount = toDecimal(this.getExpectedOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmountJSBI, inputIndex, outputIndex));
    var expectedOutputAmount = toDecimal(expectedOutputAmountJSBI);
    var impact = noSlippageOutputAmount.sub(expectedOutputAmount).div(noSlippageOutputAmount);
    return impact;
  };
  _proto.getFees = function getFees(inputTradeAmount) {
    var tradingFee = calculateFeeAmount(inputTradeAmount, this.traderFee);
    var ownerFee = calculateFeeAmount(inputTradeAmount, this.ownerFee);
    return JSBI.add(tradingFee, ownerFee);
  };
  _proto.getExpectedOutputAmount = function getExpectedOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    var inputTradeAmountLessFees = this.getInputAmountLessFees(inputTradeAmount);
    return this.getOutputAmount(tokenAmounts, inputTradeAmountLessFees, inputIndex, outputIndex);
  };
  _proto.getExpectedOutputAmountWithNoSlippage = function getExpectedOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    var inputTradeAmountLessFees = this.getInputAmountLessFees(inputTradeAmount);
    return this.getOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmountLessFees, inputIndex, outputIndex);
  };
  _proto.getInputAmountLessFees = function getInputAmountLessFees(inputTradeAmount) {
    return JSBI.subtract(inputTradeAmount, this.getFees(inputTradeAmount));
  };
  _proto.getOutputAmount = function getOutputAmount(tokenAmounts, inputTradeAmountLessFees, inputIndex, outputIndex) {
    return this.curve.exchange(tokenAmounts, inputIndex, outputIndex, inputTradeAmountLessFees, false);
  };
  _proto.getOutputAmountWithNoSlippage = function getOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmountLessFees, inputIndex, outputIndex) {
    return this.curve.computeBaseY(tokenAmounts, inputIndex, outputIndex, inputTradeAmountLessFees);
  };
  return TokenSwapStable;
}();

var TokenSwapConstantProduct = /*#__PURE__*/function () {
  function TokenSwapConstantProduct(traderFee, ownerFee, feesOnInput) {
    if (feesOnInput === void 0) {
      feesOnInput = true;
    }
    this.traderFee = void 0;
    this.ownerFee = void 0;
    this.feesOnInput = void 0;
    this.traderFee = traderFee;
    this.ownerFee = ownerFee;
    this.feesOnInput = feesOnInput;
  }
  var _proto = TokenSwapConstantProduct.prototype;
  _proto.exchange = function exchange(tokenAmounts, inputTradeAmount, outputIndex) {
    var inputIndex = outputIndex === 0 ? 1 : 0;
    var newInputTradeAmount = this.feesOnInput ? this.getAmountLessFees(inputTradeAmount) : inputTradeAmount;
    var expectedOutputAmount = this.getExpectedOutputAmount(tokenAmounts, newInputTradeAmount, inputIndex, outputIndex);
    var fees = this.getFees(this.feesOnInput ? inputTradeAmount : expectedOutputAmount);
    if (!this.feesOnInput) {
      expectedOutputAmount = this.getAmountLessFees(expectedOutputAmount);
    }
    return {
      priceImpact: this.getPriceImpact(tokenAmounts, newInputTradeAmount, expectedOutputAmount, inputIndex, outputIndex),
      fees: fees,
      expectedOutputAmount: expectedOutputAmount
    };
  };
  _proto.exchangeForExactOutput = function exchangeForExactOutput(tokenAmounts, outputTradeAmount, outputIndex) {
    var inputIndex = outputIndex === 0 ? 1 : 0;
    var newOutputTradeAmount = this.feesOnInput ? outputTradeAmount : this.getAmountPlusFees(outputTradeAmount);
    var expectedInputAmount = this.getInputAmount(tokenAmounts, newOutputTradeAmount, inputIndex, outputIndex);
    var fees = this.getFees(this.feesOnInput ? expectedInputAmount : outputTradeAmount);
    if (this.feesOnInput) {
      expectedInputAmount = this.getAmountPlusFees(expectedInputAmount);
    }
    return {
      priceImpact: this.getPriceImpactExactOutput(tokenAmounts, expectedInputAmount, newOutputTradeAmount, inputIndex, outputIndex),
      fees: fees,
      expectedInputAmount: expectedInputAmount
    };
  };
  _proto.getPriceImpact = function getPriceImpact(tokenAmounts, inputTradeAmountJSBI, expectedOutputAmountJSBI, inputIndex, outputIndex) {
    if (JSBI.equal(inputTradeAmountJSBI, ZERO) || JSBI.equal(tokenAmounts[inputIndex], ZERO) || JSBI.equal(tokenAmounts[outputIndex], ZERO)) {
      return new Decimal(0);
    }
    var noSlippageOutputAmount = toDecimal(this.getExpectedOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmountJSBI, inputIndex, outputIndex));
    var expectedOutputAmount = toDecimal(expectedOutputAmountJSBI);
    var impact = noSlippageOutputAmount.sub(expectedOutputAmount).div(noSlippageOutputAmount);
    return impact;
  };
  _proto.getPriceImpactExactOutput = function getPriceImpactExactOutput(tokenAmounts, expectedInputTradeAmountJSBI, outputAmountJSBI, inputIndex, outputIndex) {
    if (JSBI.equal(outputAmountJSBI, ZERO) || JSBI.equal(tokenAmounts[inputIndex], ZERO) || JSBI.equal(tokenAmounts[outputIndex], ZERO)) {
      return new Decimal(0);
    }
    var noSlippageInputAmount = toDecimal(this.getExpectedInputAmountWithNoSlippage(tokenAmounts, outputAmountJSBI, inputIndex, outputIndex));
    var expectedInputAmount = toDecimal(expectedInputTradeAmountJSBI);
    var impact = expectedInputAmount.sub(noSlippageInputAmount).div(noSlippageInputAmount);
    return impact;
  };
  _proto.getFees = function getFees(tradeAmount) {
    var tradingFee = calculateFeeAmount(tradeAmount, this.traderFee);
    var ownerFee = calculateFeeAmount(tradeAmount, this.ownerFee);
    return JSBI.add(tradingFee, ownerFee);
  };
  _proto.getExpectedOutputAmount = function getExpectedOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    return this.getOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex);
  };
  _proto.getExpectedOutputAmountWithNoSlippage = function getExpectedOutputAmountWithNoSlippage(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    if (JSBI.equal(tokenAmounts[inputIndex], ZERO)) {
      return tokenAmounts[outputIndex];
    }
    var expectedOutputAmountWithNoSlippage = JSBI.divide(JSBI.multiply(inputTradeAmount, tokenAmounts[outputIndex]), tokenAmounts[inputIndex]);
    if (this.feesOnInput) {
      return expectedOutputAmountWithNoSlippage;
    } else {
      return this.getAmountLessFees(expectedOutputAmountWithNoSlippage);
    }
  };
  _proto.getExpectedInputAmountWithNoSlippage = function getExpectedInputAmountWithNoSlippage(tokenAmounts, outputTradeAmount, inputIndex, outputIndex) {
    if (JSBI.equal(tokenAmounts[outputIndex], ZERO)) {
      return tokenAmounts[inputIndex];
    }
    var expectedInputAmountWithNoSlippage = JSBI.divide(JSBI.multiply(outputTradeAmount, tokenAmounts[inputIndex]), tokenAmounts[outputIndex]);
    if (this.feesOnInput) {
      return this.getAmountPlusFees(expectedInputAmountWithNoSlippage);
    } else {
      return expectedInputAmountWithNoSlippage;
    }
  };
  _proto.getAmountLessFees = function getAmountLessFees(tradeAmount) {
    return JSBI.subtract(tradeAmount, this.getFees(tradeAmount));
  };
  _proto.getAmountPlusFees = function getAmountPlusFees(tradeAmount) {
    return JSBI.add(tradeAmount, this.getFees(tradeAmount));
  };
  _proto.getOutputAmount = function getOutputAmount(tokenAmounts, inputTradeAmount, inputIndex, outputIndex) {
    var _ref = [tokenAmounts[inputIndex], tokenAmounts[outputIndex]],
      poolInputAmount = _ref[0],
      poolOutputAmount = _ref[1];
    var invariant = this.getInvariant(tokenAmounts);
    var _ceilingDivision = ceilingDivision(invariant, JSBI.add(poolInputAmount, inputTradeAmount)),
      newPoolOutputAmount = _ceilingDivision[0];
    return JSBI.subtract(poolOutputAmount, newPoolOutputAmount);
  };
  _proto.getInputAmount = function getInputAmount(tokenAmounts, outputTradeAmount, inputIndex, outputIndex) {
    var _ref2 = [tokenAmounts[inputIndex], tokenAmounts[outputIndex]],
      poolInputAmount = _ref2[0],
      poolOutputAmount = _ref2[1];
    var invariant = this.getInvariant(tokenAmounts);
    if (JSBI.greaterThanOrEqual(outputTradeAmount, poolOutputAmount)) {
      throw new Error('Insufficient liquidity to provide outputTradeAmount');
    }
    var _ceilingDivision2 = ceilingDivision(invariant, JSBI.subtract(poolOutputAmount, outputTradeAmount)),
      newPoolInputAmount = _ceilingDivision2[0];
    return JSBI.subtract(newPoolInputAmount, poolInputAmount);
  };
  _proto.getInvariant = function getInvariant(tokenAmounts) {
    return JSBI.multiply(tokenAmounts[0], tokenAmounts[1]);
  };
  return TokenSwapConstantProduct;
}();

var Fraction = function Fraction(numerator, denominator) {
  this.numerator = void 0;
  this.denominator = void 0;
  this.numerator = numerator;
  this.denominator = denominator;
};
var ZERO_FRACTION = /*#__PURE__*/new Fraction( /*#__PURE__*/JSBI.BigInt(0), /*#__PURE__*/JSBI.BigInt(1));

export { Fraction, ONE, Stable, TWO, TokenSwapConstantProduct, TokenSwapStable, ZERO, ZERO_FRACTION, abs, calculateFeeAmount, ceilingDivision, mulArray, sumArray, toDecimal };
//# sourceMappingURL=math.esm.js.map
